/**
 * The two writes on Time off (WALLIYO-0131): asking, and deciding.
 *
 * Both go through the console's ReasonDialogView, so they ask for a reason,
 * carry an Idempotency-Key generated once per click, and show the server's
 * refusal rather than guessing at one. The server checks everything these
 * forms check, because the endpoint has other callers than this screen.
 */

import type { TimeOffKind, TimeOffRequest } from "@wallyo/contracts/console/hr";
import { useState } from "react";
import { decideTimeOff, requestTimeOff } from "../../api/console-api";
import { useApi } from "../../api/context";
import { t, type MessageId } from "../../i18n";
import { ReasonDialogView, useIntentWrite } from "../team/TeamDialogs";

const KINDS: readonly { id: TimeOffKind; label: MessageId }[] = [
  { id: "annual", label: "timeOff.kind.annual" },
  { id: "sick", label: "timeOff.kind.sick" },
  { id: "unpaid", label: "timeOff.kind.unpaid" },
  { id: "parental", label: "timeOff.kind.parental" },
  { id: "other", label: "timeOff.kind.other" },
];

type Common = { onClose: () => void; onReload: () => void; onDone: (message: string) => void };

/** Whole days between two dates, inclusive of both, or 0 for a bad pair. */
export function daysBetween(startsOn: string, endsOn: string): number {
  const from = Date.parse(startsOn);
  const to = Date.parse(endsOn);
  if (Number.isNaN(from) || Number.isNaN(to) || to < from) return 0;
  return Math.round((to - from) / (24 * 60 * 60 * 1000)) + 1;
}

export function AskTimeOffDialog(props: Common) {
  const { client } = useApi();
  const write = useIntentWrite();
  const [kind, setKind] = useState<TimeOffKind>("annual");
  const [startsOn, setStartsOn] = useState("");
  const [endsOn, setEndsOn] = useState("");
  // Suggested from the dates and then editable, because a public holiday or a
  // half-week is something only the person asking knows about.
  const [days, setDays] = useState("");
  const [reason, setReason] = useState("");

  const suggested = daysBetween(startsOn, endsOn);
  const value = days === "" ? String(suggested || "") : days;
  const asked = Number(value);

  const problem =
    startsOn === "" || endsOn === ""
      ? t("timeOff.form.from")
      : suggested === 0
        ? t("timeOff.form.to")
        : !Number.isInteger(asked) || asked < 1
          ? t("timeOff.form.days")
          : null;

  return (
    <ReasonDialogView
      title={t("timeOff.form.title")}
      body={<p className="meta">{t("timeOff.form.daysHelp")}</p>}
      reason={reason}
      onReason={(next) => {
        write.edited();
        setReason(next);
      }}
      submitLabel={t("timeOff.form.submit")}
      pending={write.pending}
      problem={write.problem}
      error={write.error}
      onClose={props.onClose}
      onReload={props.onReload}
      onSubmit={() =>
        void write.run(
          problem,
          (key) =>
            requestTimeOff(
              client,
              { kind, startsOn, endsOn, days: asked, reason: reason.trim() || null },
              { idempotencyKey: key },
            ),
          ({ request }: { request: TimeOffRequest }) => props.onDone(t("timeOff.done", { days: String(request.days) })),
        )
      }
    >
      <div className="field">
        <label htmlFor="off-kind">{t("timeOff.form.kind")}</label>
        <select
          id="off-kind"
          value={kind}
          onChange={(event) => {
            write.edited();
            setKind(event.currentTarget.value as TimeOffKind);
          }}
        >
          {KINDS.map((each) => (
            <option key={each.id} value={each.id}>
              {t(each.label)}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor="off-from">{t("timeOff.form.from")}</label>
        <input
          id="off-from"
          type="date"
          value={startsOn}
          onChange={(event) => {
            write.edited();
            setStartsOn(event.currentTarget.value);
            setDays("");
          }}
        />
      </div>
      <div className="field">
        <label htmlFor="off-to">{t("timeOff.form.to")}</label>
        <input
          id="off-to"
          type="date"
          value={endsOn}
          onChange={(event) => {
            write.edited();
            setEndsOn(event.currentTarget.value);
            setDays("");
          }}
        />
      </div>
      <div className="field">
        <label htmlFor="off-days">{t("timeOff.form.days")}</label>
        <input
          id="off-days"
          type="number"
          min={1}
          value={value}
          onChange={(event) => {
            write.edited();
            setDays(event.currentTarget.value);
          }}
        />
        <span className="foot">{t("timeOff.form.daysHelp")}</span>
      </div>
    </ReasonDialogView>
  );
}

export function DecideTimeOffDialog(props: Common & { request: TimeOffRequest; approve: boolean }) {
  const { client } = useApi();
  const write = useIntentWrite();
  const [reason, setReason] = useState("");
  const { request, approve } = props;

  return (
    <ReasonDialogView
      title={t("timeOff.decide.title")}
      body={
        <p className="meta">
          {request.name} · {request.startsOn} → {request.endsOn} · {t("timeOff.days", { days: String(request.days) })}
          <br />
          {approve ? t("timeOff.decide.approveNote") : t("timeOff.decide.refuseNote")}
        </p>
      }
      reason={reason}
      onReason={(next) => {
        write.edited();
        setReason(next);
      }}
      submitLabel={t("timeOff.decide.submit")}
      pending={write.pending}
      problem={write.problem}
      error={write.error}
      onClose={props.onClose}
      onReload={props.onReload}
      onSubmit={() =>
        void write.run(
          reason.trim().length === 0 ? t("timeOff.decide.approveNote") : null,
          (key) =>
            decideTimeOff(
              client,
              request.timeOffRequestId,
              { approve, reason: reason.trim(), version: request.version },
              { idempotencyKey: key },
            ),
          ({ request: decided }: { request: TimeOffRequest }) =>
            props.onDone(
              decided.status === "approved"
                ? t("timeOff.approvedDone", { name: decided.name })
                : t("timeOff.refusedDone", { name: decided.name }),
            ),
        )
      }
    />
  );
}
